/**
 * Resource Metadata Configuration
 *
 * This file defines the configuration for ResourceMetadataEditor.
 * It is completely separate from courseMetadataConfig.ts to maintain clean separation.
 *
 * Resource types and activity types are loaded dynamically via resourceTypesUtil.ts
 * which reads from resourceTypes.json.
 */

import {
  getActivityTypesForResourceType,
  type ResourceTypeName
} from './resourceTypesUtil';

/**
 * Resource Types - Maps lowercase route query param to display labels
 */
export const RESOURCE_TYPES = Object.freeze({
  video: 'Video',
  webinar: 'Webinar',
  guide: 'Guide',
  template: 'Template',
  blog: 'Blog',
  playlist: 'Playlist'
});

/**
 * Section Configuration for ResourceMetadataEditor
 */
export const SECTION_CONFIG = Object.freeze({
  sectionTitle: {
    title: 'Title & Category',
    order: 1
  },
  sectionType: {
    title: 'Type',
    order: 2
  },
  sectionDescriptions: {
    title: 'Description & Objectives',
    order: 3
  },
  sectionAudience: {
    title: 'Audience',
    order: 4
  },
  sectionInfo: {
    title: 'Additional Info',
    order: 5
  },
  sectionAssets: {
    title: 'Assets',
    order: 6
  }
});

/**
 * Field Configuration for ResourceMetadataEditor
 *
 * Key differences from CourseMetadataEditor:
 * - No portalTitle, tagline, shortDescription, howTaken
 * - No assessment fields (CompletionCriteria, passMark)
 * - No accreditations (accredited, hkcpdHours, hkrmeHours)
 * - No vendor, groupCourseId, redundancy
 * - Has resourceType/resourceTypeId, activityType/activityTypeId
 * - Has resourceUrl, contentType
 * - Has videoCaptionUrl, videoTranscriptURL (Video only)
 * - Uses objectivesHTML instead of objectives
 * - Uses additionalInfo.descriptionHTML in addition to description
 * - flagship is string ("false"/"true"), not boolean
 *
 * Field Visibility Logic:
 * - showFor: null = Show for all resource types
 * - showFor: ['video', 'webinar'] = Show only for Video and Webinar
 * - requiredFor: ['video'] = Required only for Video
 */
export const FIELD_CONFIG = Object.freeze({
  // Section 1: Title & Category
  title: {
    section: 'sectionTitle',
    required: true,
    defaultValue: '',
    showFor: null, // All resource types
    requiredFor: null
  },
  suiteTitle: {
    section: 'sectionTitle',
    required: true,
    defaultValue: '',
    showFor: null,
    requiredFor: null
  },
  topicTitle: {
    section: 'sectionTitle',
    required: true,
    defaultValue: '',
    showFor: null,
    requiredFor: null
  },

  // Section 2: Type & Features
  resourceType: {
    // String: "Video", "Webinar", "Guide", "Template", "Blog"
    // Read-only in ResourceMetadataEditor (set from route query param)
    section: 'sectionType',
    required: true,
    defaultValue: '',
    showFor: null, // All resource types
    requiredFor: null
  },
  resourceTypeId: {
    // Number: 1-5 (automatically set based on resourceType selection)
    section: 'sectionType',
    required: true,
    defaultValue: null,
    showFor: null,
    requiredFor: null
  },
  activityType: {
    // String: Selected from hierarchy based on resourceType
    section: 'sectionType',
    required: true,
    defaultValue: '',
    showFor: null,
    requiredFor: null
  },
  activityTypeId: {
    // Number: Automatically set based on activityType selection
    section: 'sectionType',
    required: true,
    defaultValue: null,
    showFor: null,
    requiredFor: null
  },
  level: {
    section: 'sectionType',
    required: true,
    defaultValue: 'All Levels', // Default to "All Levels" for all resources
    showFor: null,
    requiredFor: null
  },
  contentType: {
    // Content type dropdown: doc, docx, dotx, HLS, HTML, pdf, xlsx, N/A - not applicable to Playlist
    section: 'sectionType',
    required: false,
    defaultValue: null,
    showFor: ['video', 'webinar', 'guide', 'template', 'blog'], // Not shown for Playlist
    requiredFor: null
  },
  duration: {
    // Format: HH:MM:SS (different from courses which use HH:MM)
    section: 'sectionType',
    required: true,
    defaultValue: '01:00:00', // Default to 1 hour for Video and Webinar
    showFor: ['video', 'webinar'], // Only Video and Webinar
    requiredFor: ['video', 'webinar']
  },

  // Section 3: Description & Objectives
  'additionalInfo.description': {
    // Plain text description (derived from descriptionHTML for backwards compatibility)
    section: 'sectionDescriptions',
    required: true,
    defaultValue: '',
    showFor: null,
    requiredFor: null
  },
  'additionalInfo.descriptionHTML': {
    // HTML formatted description (user writes plain text, converts to HTML) - source of truth
    section: 'sectionDescriptions',
    required: true,
    defaultValue: '<p>Write a resource description</p>',
    showFor: null,
    requiredFor: null
  },
  objectivesHTML: {
    // HTML formatted objectives (note: field name is objectivesHTML, not objectives)
    section: 'sectionDescriptions',
    required: false,
    defaultValue: null,
    showFor: null,
    requiredFor: null
  },

  // Section 4: Audience
  'additionalInfo.Industry': {
    section: 'sectionAudience',
    required: true,
    defaultValue: '',
    showFor: null,
    requiredFor: null
  },
  'additionalInfo.industryId': {
    section: 'sectionAudience',
    required: false,
    defaultValue: '',
    showFor: null,
    requiredFor: null
  },
  'additionalInfo.Jurisdiction': {
    section: 'sectionAudience',
    required: true,
    defaultValue: [],
    showFor: null,
    requiredFor: null
  },
  'additionalInfo.jurisdictionId': {
    section: 'sectionAudience',
    required: false,
    defaultValue: [],
    showFor: null,
    requiredFor: null
  },
  targetLearners: {
    // Root-level array - source of truth
    section: 'sectionAudience',
    required: true,
    defaultValue: [],
    showFor: null,
    requiredFor: null
  },
  'resourceMaster.TargetLearners': {
    // Comma-separated string - derived from targetLearners for backwards compatibility
    section: 'sectionAudience',
    required: false,
    defaultValue: '',
    showFor: null,
    requiredFor: null
  },

  // Section 5: Additional Info
  retired: {
    section: 'sectionInfo',
    required: true,
    defaultValue: false,
    showFor: null,
    requiredFor: null
  },
  flagship: {
    // String: "false" or "true" (not boolean)
    section: 'sectionInfo',
    required: true,
    defaultValue: 'false',
    showFor: null,
    requiredFor: null
  },
  'additionalInfo.triggerWarning': {
    section: 'sectionInfo',
    required: true,
    defaultValue: false,
    showFor: null,
    requiredFor: null
  },
  'additionalInfo.qualityIndex': {
    section: 'sectionInfo',
    required: false,
    defaultValue: null,
    showFor: null,
    requiredFor: null
  },
  'additionalInfo.relatedTo': {
    section: 'sectionInfo',
    required: false,
    defaultValue: null,
    showFor: null,
    requiredFor: null
  },
  'additionalInfo.legislation': {
    section: 'sectionInfo',
    required: false,
    defaultValue: null,
    showFor: null,
    requiredFor: null
  },

  // Section 6: Assets
  resourceUrl: {
    // Resource URL - required for all except Playlist (playlists reference other resources via hasResourceIds)
    section: 'sectionAssets',
    required: true,
    defaultValue: null,
    showFor: ['video', 'webinar', 'guide', 'template', 'blog'], // Not shown for Playlist
    requiredFor: ['video', 'webinar', 'guide', 'template', 'blog']
  },
  thumbnailURL: {
    // Thumbnail image URL - required for all product types
    section: 'sectionAssets',
    required: true,
    defaultValue: '', // Will be set dynamically based on vinciResourceId
    showFor: null, // All product types
    requiredFor: null // Required for all
  },
  videoTranscriptURL: {
    // PDF transcript URL - optional, video only
    section: 'sectionAssets',
    required: false,
    defaultValue: '',
    showFor: ['video'], // Only Video
    requiredFor: null // Optional
  },
  videoCaptionUrl: {
    // VTT file URL - optional, video only (note: lowercase 'u' in 'Url' to match database)
    section: 'sectionAssets',
    required: false,
    defaultValue: '',
    showFor: ['video'], // Only Video
    requiredFor: null // Optional
  },
  hasResourceIds: {
    // Ordered array of video resource IDs in this playlist - root level, playlist only
    section: 'sectionAssets',
    required: false,
    defaultValue: [],
    showFor: ['playlist'], // Only Playlist
    requiredFor: null
  }
});

/**
 * Helper function to get activity types for a resource type from route param
 * This uses the resourceTypesUtil to dynamically load the hierarchy
 */
export const getActivityTypesForResourceTypeFromRoute = (
  resourceTypeName: string
): Array<{ name: string; activityTypeId: number }> => {
  // Convert route param to ResourceTypeName if needed
  const normalized =
    resourceTypeName.charAt(0).toUpperCase() +
    resourceTypeName.slice(1).toLowerCase();
  return getActivityTypesForResourceType(normalized as ResourceTypeName);
};
